import { Box, Flex, HStack, Stack, Text } from '@chakra-ui/react';

import SolidButton from '@/client/components/shared/buttons/SolidButton';
import { useTranslation } from 'react-i18next';
import {useExpertsStore} from '../../expertsStore';

interface ExpertIntegrationContainerHeaderProps {
  /** Whether the integration is disabled @default false */
  isDisabled?: boolean;
  /** Whether to render the setup button @default false */
  renderSetupButton?: boolean;
  /** Whether to render the integration status @default false */
  renderIntegrationStatus?: boolean;
  /** Whether the integration is enabled @default false */
  integrationEnabled?: boolean;
  /** Callback function to manually sync @default false */
  onManualSync?: () => void;
  /** Last synced date @default false */
  lastSynced?: string;
  /** The name of the integration @default '' */
  integrationName?: string;
  /** The description of the integration @default '' */
  integrationDescription?: string;
  /** Callback function to handle the setup button click */
  handleOnSetupButtonClick: () => void;
  /** The icon of the integration */
  integrationIcon: React.ReactNode;
}

export default function ExpertIntegrationContainerHeader({
  isDisabled = false,
  renderSetupButton = false,
  renderIntegrationStatus = false,
  integrationEnabled = false,
  onManualSync = undefined,
  lastSynced = undefined,
  integrationName,
  integrationDescription,
  handleOnSetupButtonClick,
  integrationIcon,
}: ExpertIntegrationContainerHeaderProps) {
  const { t } = useTranslation();
  const { isSyncingExpert: isSyncing } = useExpertsStore();
  return (
    <Stack spacing={2}>
      <Flex justifyContent="space-between" alignItems="center">
        {integrationIcon}

        {renderSetupButton && (
          <SolidButton onClick={handleOnSetupButtonClick}>
            {t('admin.aiAgent.experts.form.integrations.container.header.title')}
          </SolidButton>
        )}

        {isDisabled && (
          <Box
            paddingX={3}
            paddingY={2}
            backgroundColor="neutral.50"
            borderRadius="8px"
            fontSize="12px"
            fontWeight={500}
            color="neutral.600"
            textTransform="uppercase"
          >
            {t('admin.aiAgent.experts.form.integrations.container.header.comingSoon')}
          </Box>
        )}

        {renderIntegrationStatus && !isDisabled && !onManualSync && (
          <Box
            paddingX={3}
            paddingY={2}
            backgroundColor="neutral.50"
            borderRadius="8px"
            fontSize="12px"
            fontWeight={500}
            color="neutral.600"
          >
            {integrationEnabled
              ? t('admin.aiAgent.experts.form.integrations.container.header.enabled')
              : t('admin.aiAgent.experts.form.integrations.container.header.notEnabled')}
          </Box>
        )}

        {!!onManualSync && !isDisabled && integrationEnabled && (
          <HStack spacing={2}>
            <SolidButton onClick={onManualSync} size="sm">
              {isSyncing ? 'Syncing Updates...' : 'Sync Manually'}
            </SolidButton>
            {!!lastSynced && (
              <Box
                paddingX={3}
                paddingY={2}
                backgroundColor="neutral.50"
                borderRadius="8px"
                fontSize="12px"
                fontWeight={500}
                color="neutral.600"
              >
                Last Synced {new Date(lastSynced).toLocaleString('default', { day: 'numeric', month: 'short', year: 'numeric' })}
              </Box>
            )}
          </HStack>
        )}
      </Flex>

      <Text fontSize="16px" fontWeight={600} color="baseBlack">
        {integrationName}
      </Text>

      <Text fontSize="14px" fontWeight={500} color="neutral.600">
        {integrationDescription}
      </Text>
    </Stack>
  );
}
